import React, { useMemo, useState } from "react";
import { X } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/redux/hooks/hooks";
import ScriptWritingIcon from "@/assets/icons/authentication/skills/ScriptWritting.svg";
import VoiceOversIcon from "@/assets/icons/authentication/skills/VoiceOvers.svg";
import ActingIcon from "@/assets/icons/authentication/skills/Acting.svg";
import VideoGraphyIcon from "@/assets/icons/authentication/skills/VideoGraphy.svg";
import VideoEditingIcon from "@/assets/icons/authentication/skills/VideoEditing.svg";
import ModelingIcon from "@/assets/icons/authentication/skills/Modeling.svg";
import OthersPlusIcon from "@/assets/icons/authentication/skills/OthersPlus.svg";
import ProfileVideoPhotos from "@/assets/images/auth/profile-video-upload.png";
import UploadIcon from "@/assets/icons/authentication/UploadOrange.svg";
import LinkPlus from "@/assets/icons/authentication/LinkPlus.svg";
import { updateV2Field } from "@/redux/feature/creatorApplicationV2/creatorApplicationSliceV2";

const SkillsSection = () => {
  const dispatch = useAppDispatch();
  const skillsMulti =
    useAppSelector((s) => s.signUpV2.v2Extras.skillsMulti) || [];

  const [isOtherOpen, setIsOtherOpen] = useState(false);
  const [otherSkillInput, setOtherSkillInput] = useState("");

  // -------------------- Skills UI data --------------------
  const skillOptions = useMemo(
    () => [
      {
        key: "script_writing",
        label: "Script Writing",
        icon: <ScriptWritingIcon />,
      },
      { key: "voice_overs", label: "Voiceovers", icon: <VoiceOversIcon /> },
      { key: "acting", label: "Acting", icon: <ActingIcon /> },
      {
        key: "videography_dop",
        label: "Videography (DOP)",
        icon: <VideoGraphyIcon />,
      },
      {
        key: "video_editing",
        label: "Video Editing",
        icon: <VideoEditingIcon />,
      },
      { key: "modelling", label: "Modelling", icon: <ModelingIcon /> },
      { key: "others", label: "Others", icon: <OthersPlusIcon /> },
    ],
    [],
  );

  const normalize = (v: string) => v.trim().replace(/\s+/g, " "); // collapse multiple spaces

  // Toggle for preset options ONLY (not "others")
  const togglePresetSkill = (key: string) => {
    const exists = skillsMulti.includes(key);
    const next = exists
      ? skillsMulti.filter((k) => k !== key)
      : [...skillsMulti, key];
    dispatch(updateV2Field("skillsMulti", next));
  };

  // Add custom skill into skillsMulti
  const addCustomSkill = () => {
    const value = normalize(otherSkillInput);
    if (!value) return;

    const lower = value.toLowerCase();
    const alreadyExists = skillsMulti.some(
      (s) => String(s).toLowerCase() === lower,
    );
    if (alreadyExists) {
      setOtherSkillInput(""); // optional: clear anyway for smooth UX
      return;
    }

    const next = [...skillsMulti, value];
    dispatch(updateV2Field("skillsMulti", next));
    setOtherSkillInput("");
  };

  const removeSkill = (value: string) => {
    const next = skillsMulti.filter((s) => s !== value);
    dispatch(updateV2Field("skillsMulti", next));
  };

  // Identify which skills are preset vs custom
  const presetKeySet = useMemo(() => {
    return new Set(
      skillOptions.filter((o) => o.key !== "others").map((o) => o.key),
    );
  }, [skillOptions]);

  const customSkills = skillsMulti.filter((s) => !presetKeySet.has(s));

  const canAdd = normalize(otherSkillInput).length > 0;

  return (
    <div className="space-y-8">
      <div>
        <label className="text-sm tracking-normal mb-2 block">
          <span className="text-[#121217] font-medium">
            Select all skills you have{" "}
          </span>
          <span className="text-[#475467]">(Select all that apply)</span>
        </label>

        {/* Preset skills chips */}
        <div className="flex flex-wrap gap-2">
          {skillOptions.map((opt) => {
            const isOthers = opt.key === "others";

            const active = isOthers
              ? isOtherOpen
              : skillsMulti.includes(opt.key);

            return (
              <div
                key={opt.key}
                onClick={() => {
                  if (isOthers) {
                    setIsOtherOpen((p) => !p);
                    return;
                  }
                  togglePresetSkill(opt.key);
                }}
                className={`border py-[8.5px] px-[7px] flex items-center gap-1 rounded-lg cursor-pointer select-none
                  ${active ? "border-[#FF6600] bg-[#FFF4EC]" : "border-[#D8D8DD]"}`}
              >
                {opt.icon}
                <p className="text-xs tracking-[-0.5px]">{opt.label}</p>
              </div>
            );
          })}
        </div>

        {/* Custom skills chips (added via Others) */}
        {customSkills.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {customSkills.map((s) => (
              <div
                key={s}
                className="border border-[#FF6600] bg-[#FFF4EC] rounded-lg px-2 py-1 flex items-center gap-2"
              >
                <span className="text-xs tracking-[-0.3px]">{s}</span>
                <button
                  type="button"
                  onClick={() => removeSkill(s)}
                  className="p-0.5 rounded hover:bg-black/5"
                  aria-label={`Remove ${s}`}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Others input row */}
        {isOtherOpen && (
          <div className="mt-3">
            <div className="flex gap-2 items-center">
              <input
                value={otherSkillInput}
                onChange={(e) => setOtherSkillInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addCustomSkill();
                  }
                }}
                placeholder="Type a skill (e.g. Lighting, Directing)"
                className="w-full border border-[#D8D8DD] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#FF6600]"
              />

              <button
                type="button"
                onClick={addCustomSkill}
                disabled={!canAdd}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition
                  ${canAdd ? "bg-[#FF6600] text-white hover:opacity-90" : "bg-[#F2F4F7] text-[#98A2B3] cursor-not-allowed"}`}
              >
                Add
              </button>
            </div>

            <p className="text-xs text-[#667085] mt-1">
              Press <span className="font-medium">Enter</span> to add quickly.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SkillsSection;
